import { FC, useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import useDC from '@eneco/dc/useDC';
import { I18nProvider, useTranslation, MessageContext } from '@eneco/i18n';
import { NotificationBoxProps } from '@eneco/sparky/types';
import { useTracking } from '@eneco/tracking';

import { useMeterReadingsFlow } from '../../state/MeterReadingsFlow.machine';
import { Steps } from '../../tracking';
type ErrorState = {
  notificationType: NotificationBoxProps['variant'];
  title: string;
  content: string;
  funnelComplete?: boolean;
};

const errors = (stateValue: string, t: (key: string, context?: MessageContext) => string): ErrorState | undefined => {
  switch (stateValue) {
    case 'ERROR_YEARNOTE':
      return {
        notificationType: 'info',
        title: t('mvsErrorYearnoteTitle'),
        content: t('mvsErrorYearnoteContent'),
        funnelComplete: true,
      };
    case 'ERROR_SWITCH':
      return {
        notificationType: 'info',
        title: t('mvsErrorSwitchTitle'),
        content: t('mvsErrorSwitchContent'),
        funnelComplete: true,
      };
    case 'ERROR_ENDNOTE':
      return {
        notificationType: 'info',
        title: t('mvsErrorEndnoteTitle'),
        content: t('mvsErrorEndnoteContent'),
        funnelComplete: true,
      };

    case 'ERROR_READING_ID_UNKNOWN':
      return {
        notificationType: 'warning',
        title: t('readingIdUnknownTitle'),
        content: t('readingIdUnknownContent'),
      };

    case 'ERROR_INVALID_COMBINATION':
      return {
        notificationType: 'warning',
        title: t('invalidCombinationTitle'),
        content: t('invalidCombinationContent'),
      };

    case 'ERROR_COMBINATION_EXPIRED':
      return {
        notificationType: 'warning',
        title: t('combinationExpiredTitle'),
        content: t('combinationExpiredContent'),
      };

    case 'ERROR_DATA_ALREADY_SET':
      return {
        notificationType: 'info',
        title: t('dataAlreadySetTitle'),
        content: t('dataAlreadySetContent'),
        funnelComplete: true,
      };

    case 'ERROR_UNKNOWN':
      return {
        notificationType: 'warning',
        title: t('somethingElseTitle'),
        content: t('somethingElseContent'),
      };
    default:
      return undefined;
  }
};

export const ReadingStepErrors: FC = () => {
  const { label } = useDC();

  return (
    <I18nProvider dictionary={locale => import(`./content/ReadingIdStepErrors.${label}.${locale}.json`)}>
      <ReadingStepErrorsComponent />
    </I18nProvider>
  );
};

export const ReadingStepErrorsComponent: FC = () => {
  const { t } = useTranslation();
  const { state } = useMeterReadingsFlow();
  const { trackFunnelCompleted } = useTracking();
  const { value } = state;
  const stateValue = (value as { READING_ID: string }).READING_ID;

  const error = errors(stateValue, t);
  useEffect(() => {
    if (error?.funnelComplete) {
      trackFunnelCompleted({
        step: `${Steps['READING_ID']} - ${error.title} - ${stateValue}`,
        funnel: state.context.funnelName,
      });
    }
  }, [state.context.funnelName, trackFunnelCompleted, error, stateValue]);

  if (!error) return null;

  return (
    <TrackedNotificationBox
      variant={error.notificationType}
      title={error.title}
      label={`${error.title} - ${stateValue}`}
      text={<RichText html={error.content} />}
      isAlert={true}
    />
  );
};
